import { Fragment } from "react";
import { Provider } from "@aviv-unified-frontend/shell";
import type { PropsWithChildren } from "react";
import type { ProviderProps } from "@aviv-unified-frontend/shell";
import { createMonitoringContext } from "@aviv-unified-frontend/runtime/monitoring";
import type { ApplicationRegistryItem } from "@aviv-unified-frontend/registry";

import { BRAND_CONFIGURATION } from "../configs/brands";
import { registry } from "../configs/registry";
import type { PortalConfig } from "../configs/brands/types";

import type { Errors, InitialApplication } from "./utils/useGlobalStore";
import { AirshipLoader } from "./components/Airship";
import { IS_DEV_LIKE_ENVIRONMENT } from "./constants/environment";
import { Router } from "./components/Router";
import { Layout } from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { DevTools } from "./components/DevTools";
import { QualtricsLoader } from "./components/Qualtrics";
import {
  OverlayOrchestratorProvider,
  useInitOverlayOrchestrator,
} from "./components/OverlayOrchestrator";
import {
  GlobalStoreProvider,
  createGlobalStoreContext,
} from "./utils/useGlobalStore";
import { useLoadTranslations } from "./utils/useLoadTranslations";
import { createShellLifecycleContext } from "./utils/createShellLifecycleContext";
import { RuntimeApplicationProvider } from "./federation/runtime";
import { AppBanner } from "./components/AppBanner";
import { MetaDatas } from "./components/MetaDatas";
import { InitialApplicationScript } from "./federation/InitialApplicationScript";
import { useDatadogConsent } from "./utils/consentManagement";
import { ErrorOverlay } from "./components/ErrorOverlay";

export type ShellProps = Pick<
  ProviderProps,
  | "authenticationContext"
  | "fetcherContext"
  | "headContext"
  | "i18nContext"
  | "routerContext"
  | "monitoringContext"
> & {
  initialApplication?: InitialApplication | undefined;
  headerFooterConfig?: PortalConfig | undefined;
  applicationId: ApplicationRegistryItem["id"] | undefined;
  useEnvironmentContext: () => ProviderProps["environmentContext"];
  errors: Errors | undefined;
};

const lifecycleContext = createShellLifecycleContext();

const monitoringContext = createMonitoringContext({
  useConsent: useDatadogConsent,
});

const consentContext = {
  id: BRAND_CONFIGURATION.consentManagement.id,
};

const brandContext = {
  id: BRAND_CONFIGURATION.id,
  origin: BRAND_CONFIGURATION.origin,
};

export const Shell = ({
  initialApplication,
  authenticationContext,
  fetcherContext,
  headContext,
  i18nContext,
  routerContext,
  headerFooterConfig,
  applicationId,
  errors,
  useEnvironmentContext,
}: ShellProps) => {
  return (
    <GlobalStoreProvider
      context={createGlobalStoreContext({
        initialApplication,
        headerFooterConfig,
        applicationId,
        errors,
      })}
    >
      <OverlayOrchestratorProvider>
        <ShellProvider
          authenticationContext={authenticationContext}
          fetcherContext={fetcherContext}
          headContext={headContext}
          i18nContext={i18nContext}
          routerContext={routerContext}
          useEnvironmentContext={useEnvironmentContext}
        />
      </OverlayOrchestratorProvider>
    </GlobalStoreProvider>
  );
};

const ShellProvider = ({
  authenticationContext,
  fetcherContext,
  headContext,
  i18nContext,
  routerContext,
  useEnvironmentContext,
}: Omit<ShellProps, "applicationId" | "headerFooterConfig" | "errors">) => {
  const ConditionalRuntimeProvider = IS_DEV_LIKE_ENVIRONMENT
    ? RuntimeApplicationProvider
    : Fragment;

  const environmentContext = useEnvironmentContext();

  return (
    <Provider
      authenticationContext={authenticationContext}
      brandContext={brandContext}
      consentContext={consentContext}
      environmentContext={environmentContext}
      fetcherContext={fetcherContext}
      headContext={headContext}
      i18nContext={i18nContext}
      lifecycleContext={lifecycleContext}
      routerContext={routerContext}
      theme={BRAND_CONFIGURATION.theme}
      registry={registry}
      monitoringContext={monitoringContext}
      featureFlaggingContext={BRAND_CONFIGURATION.featureFlags}
    >
      <InitializeShell>
        <ErrorBoundary name="head">
          <MetaDatas />
          <InitialApplicationScript />
        </ErrorBoundary>
        <ErrorBoundary name="layout">
          <Layout>
            <AppBanner />
            <ConditionalRuntimeProvider>
              <ErrorBoundary name="router">
                <Router />
              </ErrorBoundary>
              {IS_DEV_LIKE_ENVIRONMENT && <DevTools />}
            </ConditionalRuntimeProvider>
          </Layout>
          <QualtricsLoader />
          <AirshipLoader />
        </ErrorBoundary>
        {IS_DEV_LIKE_ENVIRONMENT && <ErrorOverlay />}
      </InitializeShell>
    </Provider>
  );
};

/**
 * Slotted component to load translations.
 * This pattern is used to prevent unnecessary re-renders of the Shell content when translations are loaded, updated...
 */
const InitializeShell = ({ children }: PropsWithChildren) => {
  useLoadTranslations();
  useInitOverlayOrchestrator();

  return children;
};
